import type {ParsedFilters, SidebarFacetOptions, SidebarParsedFilters, SortDirection} from './filter-utils'
import {
  normalizeDetailedStatus,
  parseFilterQuery,
  buildFilterQuery,
  parseSidebarFilterQuery,
  buildSidebarGroupFilterQuery,
  ME_ALIAS,
  VALID_AGENT_TYPES,
  FILTER_STATUS_NEEDS_ATTENTION,
} from './filter-utils'
import type {ActiveTab} from '../types/filter-types'

/**
 * Reads session filter state from URL search params.
 * Prefers the unified `?q=` param; falls back to individual params for backward compatibility.
 */
export function readFiltersFromSearchParams(
  searchParams: URLSearchParams,
  currentUserLogin?: string,
): ParsedFilters & {hasQParam: boolean} {
  const qParam = searchParams.get('q')

  if (qParam !== null) {
    const parsed = parseFilterQuery(qParam)
    // Resolve @me to current user login
    if (currentUserLogin) {
      parsed.creators = parsed.creators.map(c => (c === ME_ALIAS ? currentUserLogin : c))
      parsed.excludedCreators = parsed.excludedCreators.map(c => (c === ME_ALIAS ? currentUserLogin : c))
    }
    return {...parsed, hasQParam: true}
  }

  // Fallback: read from individual params (backward compatibility for existing bookmarks)
  return {...readLegacyParams(searchParams, currentUserLogin), hasQParam: false}
}

/**
 * Reads filter state from individual (legacy) URL params.
 */
function readLegacyParams(searchParams: URLSearchParams, currentUserLogin?: string): ParsedFilters {
  // Status tab (is:open / is:closed / all)
  const completedParam = searchParams.get('completed')
  let status: ActiveTab = 'open'
  if (completedParam === 'true') status = 'closed'
  else if (completedParam === 'all') status = 'all'

  // Detailed statuses
  const statusParam = searchParams.get('status')
  const detailedStatuses = statusParam ? statusParam.split(',').filter(Boolean).map(normalizeDetailedStatus) : []

  // Creators
  const authorParam = searchParams.get('author')
  let creators: string[] = []
  if (authorParam && authorParam !== '*') {
    creators = authorParam
      .split(',')
      .filter(Boolean)
      .map(c => (c === ME_ALIAS && currentUserLogin ? currentUserLogin : c))
  }

  // Excluded creators
  const excludeAuthorParam = searchParams.get('exclude_author')
  const excludedCreators = excludeAuthorParam
    ? excludeAuthorParam
        .split(',')
        .filter(Boolean)
        .map(c => (c === ME_ALIAS && currentUserLogin ? currentUserLogin : c))
    : []

  // Agents
  const agents = searchParams.getAll('agent').flatMap(param => param.split(',').filter(Boolean))

  // Types (agent group filter: cloud/cli/vscode)
  const agentGroupParam = searchParams.get('agent_group')
  const types = agentGroupParam ? agentGroupParam.split(',').filter(v => VALID_AGENT_TYPES.has(v)) : []

  // Sort
  const sortParam = searchParams.get('sort')
  const sort: SortDirection = sortParam === 'oldest' ? 'oldest' : 'newest'

  // Free-text search (current ?q= usage when it's just plain text, not qualifiers)
  const freeText = ''

  // State filter (waiting_for_user → status:needs-attention)
  const stateParam = searchParams.get('state')
  if (stateParam === 'waiting_for_user' && !detailedStatuses.includes(FILTER_STATUS_NEEDS_ATTENTION)) {
    detailedStatuses.push(FILTER_STATUS_NEEDS_ATTENTION)
  }

  return {status, detailedStatuses, creators, excludedCreators, agents, types, sort, freeText}
}

/**
 * Writes filter state to URL search params as a unified `?q=` param.
 * Removes all legacy individual filter params.
 */
export function writeFiltersToSearchParams(
  parsed: ParsedFilters,
  prev: URLSearchParams,
  currentUserLogin?: string,
): URLSearchParams {
  const next = new URLSearchParams(prev)

  // Build the q= value
  const qValue = buildFilterQuery(
    parsed.status,
    parsed.creators,
    parsed.sort,
    parsed.freeText,
    parsed.detailedStatuses,
    parsed.agents,
    currentUserLogin,
    parsed.excludedCreators,
    parsed.types,
  )
  // Always write q= (even when empty) so the reader stays in q-syntax mode. An empty
  // q= parses back to status 'all'; deleting it would make readFiltersFromSearchParams
  // fall back to legacy params and default to 'open' (e.g. clearing the filter bar and
  // submitting, which means "show everything").
  next.set('q', qValue)

  // Remove all legacy filter params
  removeLegacyFilterParams(next)

  // Reset pagination when filters change
  next.delete('page')

  return next
}

/**
 * Writes filter state to URL search params as individual legacy params.
 * Used when the q= syntax feature flag is disabled.
 */
export function writeLegacyFilterParams(
  parsed: ParsedFilters,
  prev: URLSearchParams,
  _currentUserLogin?: string,
): URLSearchParams {
  const next = new URLSearchParams(prev)

  // Remove q= if present
  next.delete('q')

  // Status tab → completed param
  if (parsed.status === 'closed') next.set('completed', 'true')
  else if (parsed.status === 'all') next.set('completed', 'all')
  else next.delete('completed')

  // Creators → author param (convert current user back to login)
  if (parsed.creators.length > 0) {
    next.set('author', parsed.creators.join(','))
  } else {
    next.delete('author')
  }

  // Excluded creators
  if (parsed.excludedCreators.length > 0) {
    next.set('exclude_author', parsed.excludedCreators.join(','))
  } else {
    next.delete('exclude_author')
  }

  // Detailed statuses
  if (parsed.detailedStatuses.length > 0) {
    next.set('status', parsed.detailedStatuses.join(','))
  } else {
    next.delete('status')
  }

  // Agents
  next.delete('agent')
  for (const agent of parsed.agents) {
    next.append('agent', agent)
  }

  // Agent group / types
  if (parsed.types.length > 0) {
    next.set('agent_group', parsed.types.join(','))
  } else {
    next.delete('agent_group')
  }

  // Sort
  if (parsed.sort === 'oldest') next.set('sort', 'oldest')
  else next.delete('sort')

  // Reset pagination when filters change
  next.delete('page')

  return next
}

/**
 * Builds a q= param value for sidebar navigation links.
 * Used to construct shareable URLs for sidebar items.
 */
export function buildSidebarFilterQuery(options: {
  status?: ActiveTab
  creators?: string[]
  detailedStatuses?: string[]
  /** @deprecated Use `detailedStatuses: ['needs-attention']` instead. */
  state?: string
  currentUserLogin?: string
}): string {
  const {status = 'open', creators = [], detailedStatuses = [], state, currentUserLogin} = options
  const statuses = detailedStatuses.map(normalizeDetailedStatus)

  // Legacy support: translate state to detailedStatuses
  if (state === 'waiting_for_user' && !statuses.includes(FILTER_STATUS_NEEDS_ATTENTION)) {
    statuses.push(FILTER_STATUS_NEEDS_ATTENTION)
  }

  return buildFilterQuery(status, creators, 'newest', '', statuses, [], currentUserLogin, [], [])
}

/**
 * Updates the status (`is:`) qualifier in a `?q=` filter string for the given tab.
 *
 * For the `'all'` tab the `is:` qualifier is removed entirely. The result can be an
 * empty string when no other qualifiers remain (e.g. `is:open` → `all`); callers must
 * still write this empty value to the `q` param so the reader stays in q-syntax mode
 * (an empty `q=` parses back to status `'all'`, whereas deleting `q` falls back to the
 * legacy default of `'open'`).
 */
export function setStatusInQuery(currentQuery: string, tab: ActiveTab): string {
  const withoutIs = currentQuery.replace(/\bis:(open|closed|all)\b\s*/g, '').trim()
  return tab === 'all' ? withoutIs : `is:${tab} ${withoutIs}`.trim()
}

export function readSidebarFiltersFromSearchParams(
  searchParams: URLSearchParams,
  currentUserLogin?: string,
  {includeFacets = false}: SidebarFacetOptions = {},
): SidebarParsedFilters {
  const parsed = parseSidebarFilterQuery(searchParams.get('sq') ?? '', {includeFacets})
  if (currentUserLogin) {
    parsed.creators = parsed.creators.map(c => (c === ME_ALIAS ? currentUserLogin : c))
    parsed.excludedCreators = parsed.excludedCreators.map(c => (c === ME_ALIAS ? currentUserLogin : c))
  }
  return parsed
}

export function writeSidebarFiltersToSearchParams(
  filters: SidebarParsedFilters,
  prev: URLSearchParams,
  currentUserLogin?: string,
): URLSearchParams {
  const next = new URLSearchParams(prev)
  const sqValue = buildSidebarGroupFilterQuery(filters, currentUserLogin)
  if (sqValue) next.set('sq', sqValue)
  else next.delete('sq')
  return next
}

/**
 * Removes all legacy individual filter params from search params.
 */
export function removeLegacyFilterParams(params: URLSearchParams): void {
  params.delete('author')
  params.delete('exclude_author')
  params.delete('status')
  params.delete('agent')
  params.delete('agent_group')
  params.delete('sort')
  params.delete('completed')
  params.delete('state')
}
